// Which page belongs to which quiz and trade. Every quiz has a hub route key
// ('rateQuiz', 'employeeQuiz') and one key per trade ('rateQuizPainter'), so
// canonical URLs, hreflang and the sitemap treat each page as its own.
import { getRouteByKey, isRouteAvailable, type SiteLanguage } from '@/utils/routing';
import type { TradeCode } from './countries';

export type QuizKind = 'rateQuiz' | 'employeeQuiz' | 'vehicleQuiz' | 'quoteQuiz' | 'latePayerQuiz' | 'freeWorkQuiz' | 'hireQuiz' | 'materialQuiz';

interface TradeRoute {
  trade: TradeCode;
  /** Route key suffix shared by all quizzes: rateQuiz + Painter. */
  suffix: string;
  /** The trade's software landing page, when the site has one. */
  landingKey?: string;
  /**
   * Public Bidmio Forms calculator for this trade (forms.bidmio...). Until a
   * trade has one, the follow-up links to the forms feature page instead.
   */
  formUrl?: Partial<Record<SiteLanguage, string>>;
}

export const QUIZ_TRADES: readonly TradeRoute[] = [
  { trade: 'painter', suffix: 'Painter', landingKey: 'tradePainter' },
  { trade: 'carpenter', suffix: 'Carpenter', landingKey: 'tradeCarpenter' },
  { trade: 'electrician', suffix: 'Electrician', landingKey: 'tradeElectrician' },
  { trade: 'plumber', suffix: 'Plumber', landingKey: 'tradePlumber' },
  { trade: 'mason', suffix: 'Mason', landingKey: 'tradeMason' },
  { trade: 'roofer', suffix: 'Roofer', landingKey: 'tradeRoofer' },
];

/** Quiz trade for a trade landing page id; general contractors and service have no quiz. */
export function quizTradeFor(tradeId: string): TradeCode | null {
  return QUIZ_TRADES.some((r) => r.trade === tradeId) ? (tradeId as TradeCode) : null;
}

export function tradeRoute(trade: TradeCode): TradeRoute {
  return QUIZ_TRADES.find((r) => r.trade === trade) ?? QUIZ_TRADES[0];
}

export const quizRouteKey = (quiz: QuizKind, trade: TradeCode): string => `${quiz}${tradeRoute(trade).suffix}`;

/** Trades that have a page for this quiz on this site, with their paths. */
export function availableTrades(lang: SiteLanguage, quiz: QuizKind = 'rateQuiz'): Array<TradeRoute & { path: string }> {
  return QUIZ_TRADES.filter((r) => isRouteAvailable(`${quiz}${r.suffix}`, lang)).map((r) => ({
    ...r,
    path: getRouteByKey(`${quiz}${r.suffix}`, lang),
  }));
}

const ALL_QUIZZES: readonly QuizKind[] = ['rateQuiz', 'employeeQuiz', 'vehicleQuiz', 'quoteQuiz', 'latePayerQuiz', 'freeWorkQuiz', 'hireQuiz', 'materialQuiz'];

/**
 * The trade a page is about: its software landing page (/software-til-maler/)
 * or any of its quiz pages and result pages. Null on every other page.
 */
export function tradeForPath(pathname: string, lang: SiteLanguage): TradeCode | null {
  for (const r of QUIZ_TRADES) {
    if (r.landingKey && isRouteAvailable(r.landingKey, lang) && pathname === getRouteByKey(r.landingKey, lang)) return r.trade;
    for (const quiz of ALL_QUIZZES) {
      const key = `${quiz}${r.suffix}`;
      if (isRouteAvailable(key, lang) && pathname.startsWith(getRouteByKey(key, lang))) return r.trade;
    }
  }
  return null;
}

/** Result ("thank you") page under each trade quiz: /quiz/timepris-beregner/maler/resultat/. */
const RESULT_SLUG: Record<SiteLanguage, string> = { sk: 'vysledok', cs: 'vysledek', da: 'resultat', en: 'result', de: 'ergebnis' };

export const quizPath = (quiz: QuizKind, trade: TradeCode, lang: SiteLanguage): string =>
  getRouteByKey(quizRouteKey(quiz, trade), lang);

export const resultPath = (quiz: QuizKind, trade: TradeCode, lang: SiteLanguage): string =>
  `${quizPath(quiz, trade, lang)}${RESULT_SLUG[lang]}/`;

/**
 * Whether a quiz page is rendered at this site's own path for it. The Danish
 * routes are registered for every site, so without this a Danish slug on
 * bidmio.sk would show the Slovak quiz under a second URL.
 */
export function isOwnQuizPath(pathname: string, lang: SiteLanguage, quiz: QuizKind | 'quizHub', trade?: TradeCode, view: 'quiz' | 'result' = 'quiz'): boolean {
  const withSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
  if (quiz === 'quizHub' || !trade) return withSlash === getRouteByKey(quiz, lang);
  return withSlash === (view === 'result' ? resultPath(quiz, trade, lang) : quizPath(quiz, trade, lang));
}

export interface QuizPageRoute {
  path: string;
  quiz: QuizKind;
  trade?: TradeCode;
  view?: 'result';
}

/** Every page of every quiz on a site: each quiz's hub, its trade pages and their result pages. */
export function quizPages(lang: SiteLanguage): QuizPageRoute[] {
  return ALL_QUIZZES.flatMap((quiz) => {
    if (!isRouteAvailable(quiz, lang)) return [];
    const trades = availableTrades(lang, quiz).flatMap(({ trade, path }) => [
      { path, quiz, trade },
      { path: resultPath(quiz, trade, lang), quiz, trade, view: 'result' as const },
    ]);
    return [{ path: getRouteByKey(quiz, lang), quiz }, ...trades];
  });
}
